/* eslint-disable @typescript-eslint/no-explicit-any */
import apisauce from "apisauce";
import { BASE_URL } from "src/app.config";
import i18n from "src/i18n";

import { CONSTANTS } from "@constants/index";
import { supportEnum } from "@enums/route.enum";
import { RefreshTokenResponse } from "@models/components/auth.model";
import commonService from "@services/common.service";
import localSlice from "@slices/local.slice";
import { store } from "@store/index";
import { resolveUrl } from "@utils/apiUrlResolver.utils";

import API_URL from "./api.config";

const Create: any = (baseURL: string = BASE_URL) => {
  const api = apisauce.create({
    baseURL,
    timeout: 100000,
  });

  const setHeader = async () => {
    const apiHeader = await commonService.getApiHeader();
    api.setHeaders(apiHeader);

    if (!apiHeader["Authorization"]) {
      api.deleteHeader("Authorization");
    }
  };

  const get = async (
    url: string,
    customHeader?: { [key: string]: string },
    params?: { [key: string]: Array<number> | string | number },
    version?: string,
    signal?: AbortSignal
  ) => {
    if (customHeader) api.setHeaders(customHeader);
    else await setHeader();
    return api.get((version ?? "v1") + url, params, { signal });
  };

  const patch = async (
    url: string,
    body?: { [key: string]: string },
    customHeader?: { [key: string]: string },
    version?: string
  ) => {
    if (customHeader) api.setHeaders(customHeader);
    else await setHeader();
    return api.patch((version ?? "v1") + url, body);
  };

  const post = async (
    url: string,
    body?: { [key: string]: string },
    customHeader?: { [key: string]: string },
    version?: string
  ) => {
    if (customHeader) {
      api.setHeaders(customHeader);
    } else {
      await setHeader();
    }
    return api.post((version ?? "v1") + url, body);
  };

  const put = async (
    url: string,
    body?: { [key: string]: string },
    customHeader?: { [key: string]: string },
    version?: string
  ) => {
    if (customHeader) api.setHeaders(customHeader);
    else await setHeader();
    return api.put((version ?? "v1") + url, body);
  };

  const remove = async (
    url: string,
    body?: { [key: string]: string },
    version?: string
  ) => {
    await setHeader();
    return api.delete((version ?? "v1") + url, body);
  };

  api.axiosInstance.interceptors.response.use(
    response => {
      return response;
    },

    async function (error) {
      const originalRequest = error.config;

      if (
        error.response.status === CONSTANTS.HTTP_STATUS.UNAUTHORIZED &&
        !originalRequest._retry &&
        !error?.request?.responseURL?.includes(supportEnum.loginUser)
      ) {
        originalRequest._retry = true;
        const newToken = await refreshToken();
        if (newToken.ok && newToken.data) {
          store.dispatch(
            localSlice.actions.userAuthenticated({
              isAuthenticated: true,
              refresh_token: newToken.data.refresh_token,
              token: newToken.data.token,
            })
          );
          originalRequest.headers["Authorization"] =
            "Bearer " + newToken.data.token;
          return api.axiosInstance(originalRequest);
        } else {
          store.dispatch(localSlice.actions.clearStorage(null));
          localStorage.removeItem("persist:root");
          //localStorage.clear();
          window.location.href = "/";
        }
      }
      return Promise.reject(error);
    }
  );

  const refreshToken = async () => {
    const refresh_token = store.getState().local.refreshToken;
    const head: { [key: string]: string } = {
      Accept: "application/json",
      ["Accept-Language"]: i18n.language,
    };
    head["Authorization"] = "";
    head["Content-Type"] = "application/json";
    const response = await post(
      resolveUrl(API_URL.AUTH.REFRESH, API_URL.KIOSK.REFRESH),
      { refresh_token: refresh_token ?? "" },
      head
    );
    return response as RefreshTokenResponse;
  };

  return {
    api,
    get,
    patch,
    post,
    put,
    remove,
  };
};

export default {
  Create,
};
